import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box, Stack, Typography, TextField, Button, Chip, IconButton } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

type ChatMessage = { from: "user" | "assistant" | "system"; text: string; ts: number };

type Props = {
  wsUrl: string;
  onFormPatch: (patch: any) => void;
  onResetLocal: () => void;
  onWsCommandReady?: (sendCmd: (cmd: string) => void) => void;
  onAssistantText?: (chunk: string) => void;
};

export default function ChatWindow({
  wsUrl,
  onFormPatch,
  onResetLocal,
  onWsCommandReady,
  onAssistantText,
}: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState<"connecting" | "connected" | "disconnected">("connecting");

  const wsRef = useRef<WebSocket | null>(null);
  const formModeRef = useRef(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  // --- IMPORTANT: keep latest callbacks in refs so they don't trigger reconnects ---
  const onFormPatchRef = useRef(onFormPatch);
  const onResetLocalRef = useRef(onResetLocal);
  const onWsCommandReadyRef = useRef(onWsCommandReady);
  const onAssistantTextRef = useRef(onAssistantText);

  useEffect(() => {
    onFormPatchRef.current = onFormPatch;
  }, [onFormPatch]);

  useEffect(() => {
    onResetLocalRef.current = onResetLocal;
  }, [onResetLocal]);

  useEffect(() => {
    onWsCommandReadyRef.current = onWsCommandReady;
  }, [onWsCommandReady]);

  useEffect(() => {
    onAssistantTextRef.current = onAssistantText;
  }, [onAssistantText]);

  const retryRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const addMsg = useCallback((from: ChatMessage["from"], text: string) => {
    setMessages((ms) => [...ms, { from, text, ts: Date.now() }]);
  }, []);

  const appendAssistant = useCallback((chunk: string) => {
    setMessages((ms) => {
      const last = ms[ms.length - 1];
      if (!last || last.from !== "assistant") return ms;
      const copy = [...ms];
      copy[copy.length - 1] = { ...last, text: last.text + chunk };
      return copy;
    });
  }, []);

  const sendCmd = useCallback((cmd: string) => {
    wsRef.current?.send(cmd);
  }, []);

  // Provide sendCmd once; DOES NOT depend on wsUrl or any props
  useEffect(() => {
    onWsCommandReadyRef.current?.(sendCmd);
  }, [sendCmd]);

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const closeSocket = () => {
    try {
      wsRef.current?.close();
    } catch {}
    wsRef.current = null;
  };

  const connect = useCallback(() => {
    clearReconnectTimer();
    setStatus("connecting");

    // Close any previous socket before opening a new one
    closeSocket();

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    const scheduleReconnect = () => {
      // Only reconnect if this socket is still the current one
      if (!aliveRef.current) return;
      if (wsRef.current !== ws) return;

      setStatus("disconnected");

      const attempt = Math.min(retryRef.current++, 6);
      const delay = Math.min(1000 * Math.pow(2, attempt), 10000);

      clearReconnectTimer();
      reconnectTimerRef.current = window.setTimeout(() => {
        if (!aliveRef.current) return;
        connect();
      }, delay);
    };

    ws.onopen = () => {
      if (wsRef.current !== ws) return;
      retryRef.current = 0;
      setStatus("connected");
    };

    ws.onmessage = (evt) => {
      if (wsRef.current !== ws) return;

      const data = String(evt.data ?? "");

      
      if (formModeRef.current) {
        if (data === "{form-end}") {
          formModeRef.current = false;
          return;
        }
        if (data === "{form-start}") return;

        try {
          onFormPatchRef.current(JSON.parse(data));
        } catch {}
        return;
      }

      if (data === "{form-start}") {
        formModeRef.current = true;
        return;
      }

      // Assistant streaming markers
      if (data === "{assistant}") {
        addMsg("assistant", "");
        return;
      }
      if (data === "{/assistant}") return;

      appendAssistant(data);
      onAssistantTextRef.current?.(data);
    };

    ws.onerror = () => {
      if (wsRef.current !== ws) return;
      setStatus("disconnected");
    };

    ws.onclose = scheduleReconnect;
  }, [wsUrl, addMsg, appendAssistant]);

  useEffect(() => {
    connect();
    return () => {
      clearReconnectTimer();
      closeSocket();
    };
  }, [wsUrl]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = () => {
    const msg = input.trim();
    if (!msg) return;
    addMsg("user", msg);
    setInput("");
    wsRef.current?.send(msg);
  };

  const resetAll = () => {
    if (!window.confirm("Formular und Chat wirklich zurücksetzen?")) return;
    setMessages([]);
    setInput("");
    onResetLocalRef.current();
    wsRef.current?.send("__RESET_FORM__");
    addMsg("system", "Zurückgesetzt.");
  };

  const statusChip = useMemo(() => {
    if (status === "connected") return <Chip size="small" label="verbunden" color="success" variant="outlined" />;
    if (status === "connecting") return <Chip size="small" label="verbindet…" color="warning" variant="outlined" />;
    return <Chip size="small" label="getrennt" variant="outlined" />;
  }, [status]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Stack spacing={0.25}>
          <Typography sx={{ fontWeight: 800 }}>Chat</Typography>
          <Typography variant="caption" color="text.secondary">
            Freitext beschreiben – Felder werden übernommen
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {statusChip}
          <IconButton aria-label="Reset" onClick={resetAll}>
            <RestartAltIcon />
          </IconButton>
        </Stack>
      </Stack>

      {/* Messages */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1.5 }}>
        {messages.map((m, i) => {
          const isUser = m.from === "user";
          const bg =
            m.from === "assistant"
              ? "rgba(0,0,0,0.04)"
              : m.from === "system"
              ? "rgba(255, 193, 7, 0.15)"
              : "rgba(25, 118, 210, 0.12)";

          return (
            <Box key={i} sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", mb: 1 }}>
              <Box
                sx={{
                  maxWidth: "85%",
                  px: 1.25,
                  py: 1,
                  borderRadius: 2,
                  background: bg,
                  border: "1px solid",
                  borderColor: "divider",
                  whiteSpace: "pre-wrap",
                  fontSize: 14,
                  lineHeight: 1.35,
                }}
              >
                {m.text}
              </Box>
            </Box>
          );
        })}
        <div ref={endRef} />
      </Box>

      {/* Input */}
      <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Beschreibe die Situation…"
          fullWidth
          size="small"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <Button variant="contained" endIcon={<SendIcon />} onClick={send}>
          Senden
        </Button>
      </Stack>
    </Box>
  );
}
